import axios from 'axios';
import http from 'http';
import https from 'https';
import net from 'net';
import tls from 'tls';
import logger from '../../utils/logger';
import AXIOS_BASE_CONFIG from './axiosConfigConstants';
import {
  getActiveAxiosConfig,
  subscribeToAxiosConfigChanges,
} from './axiosConfigService';

const DEFAULT_AXIOS_CLIENT_KEY = 'default';

const axiosClientsByDomainKey = new Map();

function getParsedRequestUrl(url) {
  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname.toLowerCase();
    const pathname = parsedUrl.pathname || '/';

    return {
      host,
      pathname,
    };
  } catch (error) {
    return {
      host: '',
      pathname: '',
    };
  }
}

export function getAxiosPolicy(url, axiosConfig = getActiveAxiosConfig()) {
  const { host: domain, pathname } = getParsedRequestUrl(url);
  const requestUrlKey = `${domain}${pathname}`;
  const requestConfig =
    axiosConfig?.requestURLConfigs?.[requestUrlKey] ||
    axiosConfig?.requestDomainConfigs?.[domain] ||
    axiosConfig?.requestDefaultConfig ||
    {};
  const conectionConfig =
    axiosConfig?.conectionDomainConfigs?.[domain] ||
    axiosConfig?.conectionDefaultConfig ||
    {};

  return {
    requestTimeout: requestConfig.requestTimeout,
    retry: requestConfig?.retry?.count
      ? {
          count: requestConfig.retry.count,
          interval: requestConfig.retry.interval ?? 0,
        }
      : undefined,
    conectionConfig,
    connectionClientKey: domain || DEFAULT_AXIOS_CLIENT_KEY,
  };
}

function createTimedAgent(
  AgentClass,
  connectSocket,
  successEvent,
  timeoutMessage,
  connectionTimeoutMs,
  agentOptions,
) {
  return new AgentClass({
    ...agentOptions,
    createConnection(options, onCreate) {
      const socket = connectSocket(options);
      const timeoutHandle = setTimeout(() => {
        socket.destroy(new Error(timeoutMessage));
      }, connectionTimeoutMs);
      const cancelTimeout = () => clearTimeout(timeoutHandle);

      timeoutHandle.unref?.();

      socket.once(successEvent, () => {
        cancelTimeout();
        onCreate?.(null, socket);
      });
      socket.once('error', err => {
        cancelTimeout();
        onCreate?.(err);
      });
      socket.once('close', cancelTimeout);
      return socket;
    },
  });
}

function getConnectionAgentOptions(conectionConfig) {
  return {
    keepAlive: conectionConfig?.keepAlive,
    keepAliveMsecs: conectionConfig?.keepAliveTime,
    maxSockets: conectionConfig?.maxSocket,
    maxFreeSockets: conectionConfig?.maxFreeSocket,
  };
}

function getConnectionConfigSignature(conectionConfig) {
  return JSON.stringify({
    connnectionTimeout: conectionConfig?.connnectionTimeout,
    maxSocket: conectionConfig?.maxSocket,
    maxFreeSocket: conectionConfig?.maxFreeSocket,
    keepAlive: conectionConfig?.keepAlive,
    keepAliveTime: conectionConfig?.keepAliveTime,
  });
}

function createAxiosClient(clientKey, conectionConfig) {
  const agentOptions = getConnectionAgentOptions(conectionConfig);
  const connectionTimeoutMs = conectionConfig?.connnectionTimeout;
  const httpAgent = connectionTimeoutMs
    ? createTimedAgent(
        http.Agent,
        options => net.connect(options),
        'connect',
        `HTTP connection timeout after ${connectionTimeoutMs}ms`,
        connectionTimeoutMs,
        agentOptions,
      )
    : new http.Agent(agentOptions);
  const httpsAgent = connectionTimeoutMs
    ? createTimedAgent(
        https.Agent,
        options => tls.connect(options),
        'secureConnect',
        `HTTPS connection timeout after ${connectionTimeoutMs}ms`,
        connectionTimeoutMs,
        agentOptions,
      )
    : new https.Agent(agentOptions);

  return {
    clientKey,
    configSignature: getConnectionConfigSignature(conectionConfig),
    httpAgent,
    httpsAgent,
    axiosInstance: axios.create({
      maxRedirects: AXIOS_BASE_CONFIG.maxRedirects,
      httpAgent,
      httpsAgent,
    }),
  };
}

function destroyAxiosClient(client) {
  client?.httpAgent?.destroy();
  client?.httpsAgent?.destroy();
}

function retireAxiosClient(client, clientKey) {
  const destroyDelayMs = AXIOS_BASE_CONFIG.agentRetireGraceMs;

  const destroyTimer = setTimeout(() => {
    destroyAxiosClient(client);
  }, destroyDelayMs);

  if (typeof destroyTimer.unref === 'function') {
    destroyTimer.unref();
  }

  logger.info('Axios client retired from active pool', {
    event: 'axios_client_retired',
    connection_key: clientKey,
    retire_after_ms: destroyDelayMs,
  });
}

function ensureAxiosClient(clientKey, conectionConfig) {
  const existingClient = axiosClientsByDomainKey.get(clientKey);
  const nextConfigSignature = getConnectionConfigSignature(conectionConfig);

  if (existingClient?.configSignature === nextConfigSignature) {
    return existingClient;
  }

  const axiosClient = createAxiosClient(clientKey, conectionConfig);

  axiosClientsByDomainKey.set(clientKey, axiosClient);

  if (existingClient) {
    retireAxiosClient(existingClient, clientKey);
  }

  return axiosClient;
}

function reconcileAxiosClients(activeAxiosConfig) {
  const domainConfigs = activeAxiosConfig?.conectionDomainConfigs || {};
  const defaultConnectionConfig = activeAxiosConfig?.conectionDefaultConfig || {};
  const requiredKeys = new Set([
    DEFAULT_AXIOS_CLIENT_KEY,
    ...Object.keys(domainConfigs),
  ]);

  ensureAxiosClient(DEFAULT_AXIOS_CLIENT_KEY, defaultConnectionConfig);
  for (const domainKey of Object.keys(domainConfigs)) {
    ensureAxiosClient(domainKey, domainConfigs[domainKey]);
  }

  for (const clientKey of axiosClientsByDomainKey.keys()) {
    if (requiredKeys.has(clientKey)) continue;
    const stale = axiosClientsByDomainKey.get(clientKey);
    axiosClientsByDomainKey.delete(clientKey);
    retireAxiosClient(stale, clientKey);
  }
}

async function runAxiosRequestWithRetry(
  axiosInstance,
  method,
  url,
  axiosConfig,
  data,
  retryPolicy,
  retryableStatusCodes,
  retryableMethods,
) {
  const maxRetries = retryPolicy?.count || 0;
  const retryIntervalMs = retryPolicy?.interval || 0;
  const methodLower = String(method || '').toLowerCase();

  async function attemptRequest(attemptNumber) {
    try {
      return await axiosInstance.request({
        ...axiosConfig,
        method,
        url,
        data,
      });
    } catch (error) {
      if (
        !retryableMethods.includes(methodLower) ||
        !retryableStatusCodes.includes(error?.response?.status) ||
        attemptNumber >= maxRetries
      ) {
        throw error;
      }
      if (retryIntervalMs > 0) {
        await new Promise(r => setTimeout(r, retryIntervalMs));
      }
      return attemptRequest(attemptNumber + 1);
    }
  }

  return attemptRequest(0);
}

export function getServerAxiosContext(url) {
  const activeAxiosConfig = getActiveAxiosConfig();
  const axiosPolicy = getAxiosPolicy(url, activeAxiosConfig);
  const axiosClient = ensureAxiosClient(
    axiosPolicy.connectionClientKey,
    axiosPolicy.conectionConfig,
  );

  return {
    activeAxiosConfig,
    axiosPolicy,
    axiosInstance: axiosClient.axiosInstance,
  };
}

function axiosServerRequest(method, url, config, data) {
  const { activeAxiosConfig, axiosPolicy, axiosInstance } =
    getServerAxiosContext(url);
  const mergedConfig = { ...config };
  if (axiosPolicy.requestTimeout != null) {
    mergedConfig.timeout = axiosPolicy.requestTimeout;
  }
  return runAxiosRequestWithRetry(
    axiosInstance,
    method,
    url,
    mergedConfig,
    data,
    axiosPolicy.retry,
    activeAxiosConfig.retryableStatusCodes,
    activeAxiosConfig.retryableMethods,
  );
}

export function axiosServerGet(url, config = {}) {
  return axiosServerRequest('get', url, config, undefined);
}

export function axiosServerPost(url, data, config = {}) {
  return axiosServerRequest('post', url, config, data);
}

reconcileAxiosClients(getActiveAxiosConfig());
subscribeToAxiosConfigChanges(nextConfig => {
  reconcileAxiosClients(nextConfig);
});

export default {
  axiosServerGet,
  axiosServerPost,
  getAxiosPolicy,
  getServerAxiosContext,
};
